<template>
  <v-dialog :value="dialog" @input="close" max-width="750" scrollable>
    <v-card class="pt-3">
      <CloseDialogButton :close="close" />
      <Title
        :category="product.categories[0]"
        :department="product.parent_categories[0]"
        closeDialog="modifiers"
        class="px-10"
      />
      <v-row
        class="border-bottom py-5 px-10"
        :class="{ 'pb-0 pt-0': isScroll }"
        no-gutters
      >
        <v-col class="d-flex">
          <div
            class="align-center boxshadow d-flex image-container justify-center mr-5 no-scroll pa-2"
            :class="{ scroll: isScroll }"
          >
            <img
              v-if="product.image"
              :alt="product.name"
              :src="product.image"
              class="modifier-product-image-size"
              loading="lazy"
            />
            <NoImageAvailable
              v-else
              :departmentName="
                getProductDepartmentName(
                  product.parent_categories,
                  product.categories,
                )
              "
              :size="'md'"
            />
            <CornerRibbon
              v-if="
                !isRedeemable() &&
                ((product.pricebook_info.promotions &&
                  product.pricebook_info.promotions.length) ||
                  product.new_product)
              "
              :newItem="
                !(
                  product.pricebook_info.promotions &&
                  product.pricebook_info.promotions.length
                )
              "
              large
            />
          </div>
          <div class="d-flex flex-column justify-space-between">
            <div class="line-height-1">
              <div class="font-size-xl">{{ product.name }}</div>
            </div>
            <div>
              <div
                class="color-suva-grey pr-2"
                :class="{ 'display-inline-block': isScroll }"
                v-if="!isRedeemable()"
              >
                Starting at
              </div>
              <UnitPriceDisplay
                :class="{ 'display-inline-block': isScroll }"
                :byweight="product.byweight"
                :type="'generic'"
                :unitPrice="product.cents"
                :loyalty="product.loyalty"
                :redeemable="isRedeemable()"
                :points="product.redemption_points_offer"
              />
              <FavoriteIcon
                :upc="product.upc"
                class="favorite-button-position"
              />
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="px-10 py-4" no-gutters>
        <v-col>
          <Description
            v-if="product.item_description"
            :description="product.item_description"
          />
        </v-col>
      </v-row>
      <v-row
        class="no-overflow"
        no-gutters
        v-scroll.self="onScroll"
        ref="modifiers"
      >
        <v-col>
          <v-row
            class="border-top-not-first-child"
            v-for="modifierGroup in product.modifierGroups"
            :key="modifierGroup.modifier_group"
            no-gutters
          >
            <v-col cols="12" class="background-color-seashell px-10 py-5">
              <div
                class="font-size-xl"
                :class="!modifierGroup.valid ? 'color-fire-engine-red' : ''"
              >
                {{ getModifierGroupHeaderText(modifierGroup) }}
              </div>
              <div class="color-suva-grey">
                {{ getModifierGroupRequirementsText(modifierGroup) }}
              </div>
            </v-col>
            <v-col class="px-10 py-2">
              <table>
                <tbody>
                  <tr
                    v-for="modifier in getModifiers(modifierGroup.modifiers)"
                    :key="`${modifierGroup.modifier_group}${modifier.upc}`"
                  >
                    <td class="pr-9 py-2">
                      <CheckboxInput
                        :countCheckedModifiers="countCheckedModifiers"
                        :handleModifierSelect="handleModifierSelect"
                        :modifier="modifier"
                        :modifierGroup="modifierGroup"
                        :modifiers="product.modifiers"
                      />
                    </td>
                    <td>
                      <span v-if="modifier.cents" class="text-center">{{
                        formatCurrency(modifier.cents)
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row align="center" class="border-top px-10 py-3" no-gutters>
        <v-col cols="2">
          <span class="font-weight-semibold">Quantity</span>
        </v-col>
        <v-col cols="10">
          <v-row>
            <v-col cols="6">
              <v-chip :color="product.qty ? 'primary' : 'secondary'">
                <v-btn
                  ref="minusBtn"
                  @click.stop="less"
                  fab
                  small
                  :class="disableMinusBtn() ? 'disable-minus-btn' : ''"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
                <div
                  :class="product.qty ? 'color-white' : 'color-black-magic'"
                  class="quantity-display-width text-center"
                >
                  {{ product.qty }}
                </div>
                <v-btn @click.stop="more" fab small :disabled="disableMore()">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </v-chip>
            </v-col>
            <v-col class="text-right" cols="6">
              <div @click.stop="update">
                <v-btn
                  color="success"
                  height="52"
                  width="248"
                  :disabled="!readyToAddToCart"
                  large
                  rounded
                >
                  <div
                    class="d-flex full-width font-size-md font-weight-semibold justify-space-between text-capitalize"
                  >
                    <span v-if="!isRedeemable()">{{
                      `${product.item_id ? "update in" : "add to"} cart`
                    }}</span>
                    <span v-else>Redeem</span>
                    <span v-if="product.redemption_points_offer_id"
                      >{{
                        product.redemption_points_offer * product.qty
                      }}
                      Points</span
                    >
                    <span v-if="product.is_redemption">Free</span>
                    <span v-if="!isRedeemable()"
                      >+{{ formatCurrency(price) }}</span
                    >
                  </div>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="px-10" no-gutters>
        <v-col offset="2" cols="10">
          <TermsList
            class="pb-3"
            v-if="
              !product.loyalty &&
              product.pricebook_info.promotions &&
              product.pricebook_info.promotions.length
            "
            :byweight="product.byweight"
            :promotions="product.pricebook_info.promotions"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="hasTieredDiscount"
        class="font-size-xs grey--text justify-center"
        no-gutters
      >
        *Tiered discounts cannot be combined with the other tiered discounts for
        this item.
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  checkModifierGroupValidity,
  getModifierGroupHeaderText,
  getModifierGroupRequirementsText,
  getProductDepartmentName,
  formatCurrency,
  getEstimatedPrice,
} from "@/util.js";
import CheckboxInput from "@/components/Inputs/Checkbox.vue";
import CloseDialogButton from "@/components/UI/CloseDialogButton.vue";
import CornerRibbon from "@/components/Product/Promotions/CornerRibbon.vue";
import Description from '@/components/Product/Description.vue';
import FavoriteIcon from "@/components/Product/FavoriteIcon.vue";
import NoImageAvailable from "@/components/Product/NoImageAvailable.vue";
import TermsList from "@/components/Product/Promotions/Terms/List.vue";
import Title from "@/components/Product/Title.vue";
import UnitPriceDisplay from "@/components/Product/UnitPriceDisplay.vue";

function initialState() {
  return {
    added: 0,
    isScroll: false,
    readyToAddToCart: false,
    sufficientQuantity: false,
    sufficientPoints: true,
    sufficientClubStars: true,
  };
}

export default {
  name: "ProductModifiersDialog",
  components: {
    CheckboxInput,
    CloseDialogButton,
    CornerRibbon,
    Description,
    FavoriteIcon,
    NoImageAvailable,
    TermsList,
    Title,
    UnitPriceDisplay,
  },
  computed: {
    ...mapState({
      dialog: (state) => state.product.dialog.modifiers,
      product: (state) => state.product.details,
    }),
    ...mapGetters("store", {
      total_points_remaining: "total_points_remaining",
      club_free_products_qty_remaining: "club_free_products_qty_remaining",
    }),
    ...mapGetters({
      hasTieredDiscount: "product/hasTieredDiscount",
      missingModifiers: "product/missingModifiers",
    }),
    price() {
      return this.getEstimatedPrice(this.product);
    },
  },
  data() {
    return initialState();
  },
  methods: {
    checkModifierGroupValidity,
    close() {
      this.$store.commit("product/CLOSE_DIALOG", "modifiers");
      Object.assign(this.$data, initialState());
    },
    countCheckedModifiers(modifierGroup) {
      /*
        need to know how many picked (checked) modifiers.upc
        are in the group (modifiersInGroup) array of objects
      */
      return this.product.modifiers.filter(
        (modifier) =>
          modifier.modifier_group === modifierGroup.modifier_group &&
          modifierGroup.modifiers.some((m) => m.upc === modifier.upc),
      ).length;
    },
    disableMore() {
      let ret =
        this.isRedeemable() &&
        (this.exceedsPoint() || this.exceedsFreeClubItems());

      return ret;
    },
    disableMinusBtn() {
      if (
        (this.product.byweight && this.product.qty < 1) ||
        (!this.product.byweight && this.product.qty < 2)
      ) {
        return true;
      }
      return false;
    },
    exceedsFreeClubItems() {
      return this.product.item_id
        ? this.club_free_products_qty_remaining(this.product.club_id) +
            -1 * this.added <
            1
        : this.club_free_products_qty_remaining(this.product.club_id) -
            this.product.qty <
            1;
    },
    exceedsPoint() {
      return (
        this.total_points_remaining -
          this.product.redemption_points_offer *
            (this.added + (this.product.item_id ? 0 : 1)) <
        this.product.redemption_points_offer
      );
    },
    formatCurrency,
    getEstimatedPrice,
    getModifierGroupHeaderText,
    getModifierGroupRequirementsText,
    getModifiers(modifiers) {
      return this.isRedeemable()
        ? modifiers.filter((m) => m.cents === 0)
        : modifiers;
    },
    getProductDepartmentName,
    async handleModifierSelect(event, modifier) {
      const checked = event.target.checked;

      // this manages selected modifiers on this product (product.modifiers)
      this.$store.dispatch("product/handleModifierSelect", {
        checked,
        modifier,
      });

      this.verifyReadyToAddToCart();
    },
    hasMissingModifiers() {
      let modifierGroups = this.product.modifierGroups.map((group) => {
        const totalCheckedInGroup = this.countCheckedModifiers(group);
        return {
          ...group,
          valid: this.checkModifierGroupValidity(group, totalCheckedInGroup),
        };
      });
      this.$store.commit(
        "product/UPDATE_PRODUCT_MODIFIER_GROUPS",
        modifierGroups,
      );
    },
    isRedeemable() {
      return (
        !!this.product.is_redemption ||
        !!this.product.redemption_points_offer_id
      );
    },
    less() {
      if (this.product.qty === 0) return;
      this.added -= 1;
      this.$store.commit("product/LESS_QTY");
      this.verifyReadyToAddToCart();
      if (this.disableMinusBtn()) {
        this.$nextTick(() => {
          this.$refs.minusBtn.$el.blur();
        });
      }
    },
    more() {
      this.added += 1;
      this.$store.commit("product/MORE_QTY");
      this.verifyReadyToAddToCart();
    },
    onScroll(e) {
      if (e.target.scrollTop > 0) this.isScroll = true;
      else this.isScroll = false;
    },
    update() {
      this.verifyReadyToAddToCart();
      if (!this.readyToAddToCart) return;

      this.$store.dispatch(
        "cart/updateModifierProductFromServerResponse",
        this.product,
      );

      this.close();
    },
    verifyReadyToAddToCart() {
      this.hasMissingModifiers();
      this.verifySufficientQuantity();
      this.verifySufficientPoints();
      this.verifySufficientClubStars();
      this.readyToAddToCart =
        this.sufficientQuantity &&
        (this.sufficientPoints || this.sufficientClubStars) &&
        !this.missingModifiers;
    },
    verifySufficientClubStars() {
      this.sufficientClubStars =
        !this.isRedeemable() ||
        this.club_free_products_qty_remaining(this.product.club_id) -
          this.product.qty >=
          0 ||
        this.product.item_id;
    },
    verifySufficientPoints() {
      this.sufficientPoints =
        !this.isRedeemable() ||
        this.total_points_remaining -
          this.product.redemption_points_offer *
            (this.added + (this.product.item_id ? 0 : 1)) >=
          0;
    },
    verifySufficientQuantity() {
      this.sufficientQuantity = this.product.qty > 0;
    },
  },
  watch: {
    dialog(open) {
      if (!open) return;
      this.$nextTick(() => {
        this.$refs.modifiers.scrollTop = 0;
      });
      this.verifyReadyToAddToCart();
      this.$store.commit("product/INITIALIZE_QTY");
    },
  },
};
</script>
<style scoped>
.boxshadow {
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
  border-radius: 8px;
}
.display-inline-block {
  display: inline-block;
}
.disable-minus-btn {
  pointer-events: none;
  background-color: var(--v-primary-lighten1) !important;
  color: #f6f5f4;
}
.favorite-button-position {
  position: absolute;
  top: 48px;
  right: 16px;
}
.image-container.scroll {
  height: 0px;
  transition:
    height 0.3s ease-out,
    width 0.3s ease-out;
  padding: 0px !important;
  width: 0px;
}
.modifier-product-image-size {
  border-radius: 4px;
  object-fit: contain;
  height: 120px;
}
.image-container.no-scroll {
  height: 100%;
  width: 22%;
  transition:
    height 0.3s ease-in,
    width 0.3s ease-in;
}
</style>
~
